import { Room, RoomEvent, Track, RemoteTrack } from 'livekit-client';
import { LIVEKIT_CONFIG } from '../config/livekit.config';
import { WAVRecorder } from '../utils/wavRecorder';

// LiveKit konfiguration
const LIVEKIT_URL = LIVEKIT_CONFIG.serverUrl;
const LIVEKIT_TOKEN_ENDPOINT = LIVEKIT_CONFIG.tokenEndpoint;

/**
 * Generer et LiveKit access token fra backend
 * Dette kalder et backend API endpoint for at få et token
 * 
 * For lokal udvikling uden backend, kan du bruge en test token fra:
 * https://livekit.io/token-generator
 */
export const createLiveKitToken = async (
  roomName: string,
  participantName: string,
  participantIdentity?: string,
  scenarioType?: string
): Promise<string> => {
  try {
    const requestBody: any = {
      roomName,
      participantName,
      participantIdentity: participantIdentity || participantName,
    };
    
    // Tilføj scenarioType hvis det er angivet
    if (scenarioType) {
      requestBody.scenarioType = scenarioType;
      console.log('🎭 Scenario valgt:', scenarioType);
    }
    
    console.log('🔵 Kalder backend endpoint:', LIVEKIT_TOKEN_ENDPOINT);
    console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(LIVEKIT_TOKEN_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('📥 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Backend fejl:', response.status, errorText);
      throw new Error(`Token endpoint returnerede ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Token modtaget:', data.token.substring(0, 50) + '...');
    console.log('✅ Room name bekræftet:', data.roomName);
    return data.token;
    
  } catch (error) {
    console.error('❌ Fejl ved hentning af LiveKit token:', error);
    throw error instanceof Error ? error : new Error('Kunne ikke hente access token');
  }
};

/**
 * Interface for training session
 */
export interface TrainingSession {
  room: Room;
  roomName: string;
  localStream: MediaStream;
  // Samtale optagelse (webm - hele samtalen med agent)
  conversationRecorder: MediaRecorder | null;
  conversationChunks: Blob[];
  // Bruger-kun optagelse (WAV - høj kvalitet, kun mikrofon)
  userWavRecorder: WAVRecorder | null;
  isRecording: boolean;
  isConnected: boolean;
}

/**
 * Start en træningssession med LiveKit
 */
export const startTrainingSession = async (
  userId: string,
  userName: string,
  scenarioType: string,
  onAgentAudioTrack?: (track: RemoteTrack) => void,
  onError?: (error: Error) => void
): Promise<TrainingSession> => {
  try {
    // 1. Opret unikt room name
    const roomName = `training-${userId}-${Date.now()}`;
    
    console.log('🚀 === STARTER TRÆNINGSSESSION ===');
    console.log('👤 User ID:', userId);
    console.log('👤 User Name:', userName);
    console.log('🏠 Room Name:', roomName);
    console.log('🎭 Scenario:', scenarioType);

    // 2. Generer LiveKit access token
    console.log('\n📞 FASE 1: Henter token fra backend...');
    const token = await createLiveKitToken(roomName, userName, userId, scenarioType);

    // 3. Get mikrofon
    console.log('\n🎤 FASE 2: Anmoder om mikrofon adgang...');
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
      },
      video: false
    });

    console.log('✅ Mikrofon adgang opnået');
    console.log('🎤 Audio tracks:', stream.getAudioTracks().length);

    // 4. Opret og connect til LiveKit room
    console.log('\n🔌 FASE 3: Connecter til LiveKit server...');
    console.log('🌐 LiveKit URL:', LIVEKIT_URL);
    
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
    });

    // Setup event listeners før connection
    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('📥 Track subscribed:', track.kind, 'fra:', participant.identity, 'source:', publication.source);
      
      if (track.kind === Track.Kind.Audio) {
        console.log('🔊 Audio track modtaget fra:', participant.identity);
        
        // Attach audio til DOM og afspil automatisk
        const audioElement = track.attach() as HTMLAudioElement;
        audioElement.autoplay = true;
        audioElement.volume = 1.0;
        audioElement.id = `audio-${participant.identity}`;
        document.body.appendChild(audioElement);
        
        console.log('✅ Agent audio element tilføjet og afspiller!');
        
        // Callback hvis den er sat
        if (onAgentAudioTrack) {
          onAgentAudioTrack(track as RemoteTrack);
        }
      }
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('👋 Participant connected:', participant.identity);
      if (participant.identity.startsWith('agent-')) {
        console.log('🤖 AGENT HAR JOINNET!');
      }
    });

    room.on(RoomEvent.TrackPublished, (publication, participant) => {
      console.log('📤 Track published:', publication.source, 'by:', participant.identity);
    });

    room.on(RoomEvent.TrackUnpublished, (publication, participant) => {
      console.log('📤 Track unpublished:', publication.source, 'by:', participant.identity);
    });

    room.on(RoomEvent.Disconnected, () => {
      console.log('❌ Disconnected fra room');
    });

    room.on(RoomEvent.ConnectionQualityChanged, (quality) => {
      console.log('📶 Connection quality:', quality);
    });

    // Connect til room
    await room.connect(LIVEKIT_URL, token);
    console.log('✅ Connected til LiveKit room:', roomName);

    // 5. Publish mikrofon til room
    console.log('\n📡 FASE 4: Publisher mikrofon til room...');
    const audioTrack = stream.getAudioTracks()[0];
    
    // VIGTIGT: Markér som microphone source så agent kan høre det!
    await room.localParticipant.publishTrack(audioTrack, {
      source: Track.Source.Microphone,
      name: 'microphone',
    });

    console.log('✅ Mikrofon publiceret som SOURCE_MICROPHONE');
    console.log('📊 Mine tracks:', room.localParticipant.audioTrackPublications.size);
    console.log('👂 Venter på at agent joiner...');
    
    // Debug: Log participants efter 2 sekunder
    setTimeout(() => {
      console.log('\n🔍 === DEBUG INFO ===');
      console.log('Remote participants i room:', room.remoteParticipants.size);
      console.log('Mine audio tracks:', room.localParticipant.audioTrackPublications.size);
      
      room.remoteParticipants.forEach((participant) => {
        console.log(`\n👤 Participant: ${participant.identity}`);
        console.log(`   Audio tracks: ${participant.audioTrackPublications.size}`);
        participant.audioTrackPublications.forEach((publication) => {
          console.log(`   - Track: ${publication.trackName}, Source: ${publication.source}, Subscribed: ${publication.isSubscribed}`);
        });
      });
      console.log('===================\n');
    }, 2000);

    // 6. Start DUAL optagelse
    console.log('\n🎬 FASE 5: Starter dual optagelse...');
    
    // 6A. Samtale optagelse (webm - hele samtalen)
    const conversationChunks: Blob[] = [];
    const conversationRecorder = new MediaRecorder(stream.clone(), {
      mimeType: 'audio/webm',
    });
    
    conversationRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        conversationChunks.push(event.data);
      }
    };

    conversationRecorder.onerror = (event) => {
      console.error('❌ Samtale recorder error:', event);
      if (onError) {
        onError(new Error('Samtale optagelsesfejl'));
      }
    };

    conversationRecorder.start(1000); // Gem data hver sekund
    console.log('✅ Samtale optagelse startet (webm format)');
    
    // 6B. Bruger mikrofon optagelse (WAV - høj kvalitet, kun bruger)
    const userWavRecorder = new WAVRecorder();
    
    // Opret separat stream KUN til WAV optagelse
    const wavStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false, // Slået fra for at få rådata
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
        sampleRate: 48000,
      },
      video: false
    });
    
    await userWavRecorder.start(wavStream, 48000);
    console.log('✅ WAV optagelse startet (48kHz mono, høj kvalitet)');
    console.log('\n✅ === TRÆNINGSSESSION KLAR MED DUAL RECORDING ===\n');

    return {
      room,
      roomName,
      localStream: stream,
      conversationRecorder,
      conversationChunks,
      userWavRecorder,
      isRecording: true,
      isConnected: true,
    };
  } catch (error) {
    console.error('Fejl ved start af træningssession:', error);
    if (onError) {
      onError(error as Error);
    }
    throw error;
  }
};

/**
 * Stop træningssession og returner kun WAV optagelse
 */
export const stopTrainingSession = async (
  session: TrainingSession
): Promise<{ userWav: Blob | null }> => {
  try {
    console.log('⏹️ Stopper træningssession...');

    let userWavBlob: Blob | null = null;

    // Stop samtale optagelse (webm) - kun for cleanup, ikke returneret
    if (session.conversationRecorder && session.isRecording) {
      session.conversationRecorder.stop();
      console.log('✅ Samtale optagelse stoppet (ikke gemt)');
    }

    // Stop WAV optagelse (kun bruger, høj kvalitet)
    if (session.userWavRecorder && session.isRecording) {
      userWavBlob = await session.userWavRecorder.stop();
      console.log('✅ Bruger WAV optagelse gemt:', userWavBlob.size, 'bytes');
    }

    // Stop alle tracks i local stream
    session.localStream.getTracks().forEach(track => track.stop());
    console.log('🎤 Mikrofon tracks stoppet');

    // Disconnect fra room
    session.room.disconnect();
    console.log('❌ Disconnected fra LiveKit room');
    
    console.log('\n📦 === WAV OPTAGELSE KLAR ===');
    console.log(`Bruger WAV (høj kvalitet): ${userWavBlob ? userWavBlob.size + ' bytes' : 'ikke tilgængelig'}`);
    console.log('=============================\n');

    return {
      userWav: userWavBlob,
    };
  } catch (error) {
    console.error('Fejl ved stop af træningssession:', error);
    throw error;
  }
};

/**
 * Attach en audio track til et HTML audio element
 */
export const attachAudioTrack = (track: RemoteTrack, elementId?: string): HTMLAudioElement => {
  const audioElement = document.getElementById(elementId || 'agent-audio') as HTMLAudioElement || new Audio();
  track.attach(audioElement);
  audioElement.play().catch(e => {
    console.error('Kunne ikke afspille audio:', e);
  });
  return audioElement;
};

/**
 * Tjek om browseren understøtter nødvendige features
 */
export const checkBrowserSupport = (): { 
  supported: boolean; 
  missingFeatures: string[] 
} => {
  const missingFeatures: string[] = [];

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    missingFeatures.push('getUserMedia');
  }

  if (typeof MediaRecorder === 'undefined') {
    missingFeatures.push('MediaRecorder');
  }

  if (typeof WebSocket === 'undefined') {
    missingFeatures.push('WebSocket');
  }

  return {
    supported: missingFeatures.length === 0,
    missingFeatures,
  };
};

